import React from "react";
import styles from "./TopContentTable.module.css";
import AnalyticsTooltip from "./AnalyticsTooltip";
import { formatInteger, formatPercent } from "../utils/formatNumber";

export interface TopContentColumn {
    key: string;
    label: string;
    numeric?: boolean;
    /** Render a percentage bar from the numeric value of this cell. */
    bar?: boolean;
}

export interface TopContentRow {
    id: string;
    label: string;
    cells: Record<string, number | string>;
}

interface TopContentTableProps {
    title: string;
    subtitle?: string;
    /** Shown when hovering the table card (no info icon). */
    help?: string;
    columns: TopContentColumn[];
    rows: TopContentRow[];
    emptyLabel: string;
    /**
     * When set, percentage bars use this denominator instead of the cell value.
     * Useful when the percentage is already in 0..100 form (% du total).
     */
    barIsPercentage?: boolean;
}

const TopContentTable: React.FC<TopContentTableProps> = ({
    title,
    subtitle,
    help,
    columns,
    rows,
    emptyLabel,
    barIsPercentage,
}) => {
    const maxByColumn = new Map<string, number>();
    for (const col of columns) {
        if (!col.bar) continue;
        let max = 0;
        for (const row of rows) {
            const v = Number(row.cells[col.key]) || 0;
            if (v > max) max = v;
        }
        maxByColumn.set(col.key, max);
    }

    const table = (
        <section className={`${styles.card} ${help ? styles.hasTooltip : ""}`}>
            <header className={styles.header}>
                <h3 className={styles.title}>{title}</h3>
                {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
            </header>
            {rows.length === 0 ? (
                <p className={styles.empty}>{emptyLabel}</p>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>{columns[0]?.label}</th>
                                {columns.slice(1).map((c) => (
                                    <th key={c.key} className={c.numeric ? styles.numeric : undefined}>
                                        {c.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.label}</td>
                                    {columns.slice(1).map((c) => {
                                        const raw = row.cells[c.key];
                                        const numeric = typeof raw === "number" ? raw : Number(raw) || 0;
                                        if (c.bar) {
                                            const max = barIsPercentage ? 100 : maxByColumn.get(c.key) || 1;
                                            const pct = Math.max(0, Math.min(100, (numeric / max) * 100));
                                            const display = barIsPercentage
                                                ? formatPercent(numeric, 1)
                                                : formatInteger(numeric);
                                            return (
                                                <td key={c.key} className={c.numeric ? styles.numeric : undefined}>
                                                    <span style={{ marginRight: 8 }}>{display}</span>
                                                    <span className={styles.bar}>
                                                        <span className={styles.barFill} style={{ width: `${pct}%` }} />
                                                    </span>
                                                </td>
                                            );
                                        }
                                        if (c.numeric) {
                                            return (
                                                <td key={c.key} className={styles.numeric}>
                                                    {formatInteger(numeric)}
                                                </td>
                                            );
                                        }
                                        return <td key={c.key}>{String(raw ?? "")}</td>;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );

    if (!help) return table;

    return <AnalyticsTooltip title={help}>{table}</AnalyticsTooltip>;
};

export default TopContentTable;
